import React from "react";
import {
  Row,
  Col,
  Card,
  Tag,
  Button,
  Typography,
  Space,
  Collapse,
  Segmented,
  Badge,
} from "antd";
import {
  FiTarget,
  FiTrendingUp,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiShield,
  FiZap,
  FiDatabase,
  FiCpu,
  FiBarChart2,
  FiUserPlus,
  FiChevronRight,
  FiActivity,
} from "react-icons/fi";
import { Link } from "react-router-dom";
import Layout from "../components/layout/Layout";

const { Title, Text } = Typography;

const demoPredictions = [
  {
    id: 1,
    homeTeam: "Manchester City",
    awayTeam: "Arsenal",
    league: "Premier League",
    date: "2025-01-15",
    predicted: "Home Win",
    probability: 67,
    actual: "2–1",
    status: "CORRECT",
  },
  {
    id: 2,
    homeTeam: "Liverpool",
    awayTeam: "Chelsea",
    league: "Premier League",
    date: "2025-01-14",
    predicted: "Away Win",
    probability: 52,
    actual: "1–1",
    status: "INCORRECT",
  },
  {
    id: 3,
    homeTeam: "Barcelona",
    awayTeam: "Real Madrid",
    league: "La Liga",
    date: "2025-01-13",
    predicted: "Draw",
    probability: 42,
    actual: "2–2",
    status: "CORRECT",
  },
  {
    id: 4,
    homeTeam: "Bayern Munich",
    awayTeam: "Dortmund",
    league: "Bundesliga",
    date: "2025-01-16",
    predicted: "Home Win",
    probability: 71,
    actual: "—",
    status: "PENDING",
  },
  {
    id: 5,
    homeTeam: "Inter",
    awayTeam: "Juventus",
    league: "Serie A",
    date: "2025-01-17",
    predicted: "Under 2.5",
    probability: 58,
    actual: "1–0",
    status: "CORRECT",
  },
  {
    id: 6,
    homeTeam: "PSG",
    awayTeam: "Lyon",
    league: "Ligue 1",
    date: "2025-01-18",
    predicted: "Home Win",
    probability: 65,
    actual: "1–2",
    status: "INCORRECT",
  },
];

const BRAND = "#004aad";

const StatusTag = ({ status }) => {
  if (status === "CORRECT")
    return (
      <Tag color="green" style={{ borderRadius: 999 }}>
        <FiCheckCircle style={{ marginRight: 6 }} /> Correct
      </Tag>
    );
  if (status === "INCORRECT")
    return (
      <Tag color="red" style={{ borderRadius: 999 }}>
        <FiXCircle style={{ marginRight: 6 }} /> Incorrect
      </Tag>
    );
  return (
    <Tag color="gold" style={{ borderRadius: 999 }}>
      <FiClock style={{ marginRight: 6 }} /> Pending
    </Tag>
  );
};

function PredictionCard({ p }) {
  return (
    <Card className="px-pred" bordered>
      <div className="px-pred__top">
        <div className="px-pred__league">{p.league}</div>
        <div className="px-pred__date">{new Date(p.date).toLocaleDateString()}</div>
      </div>

      <div className="px-pred__teams">
        <div className="team">{p.homeTeam}</div>
        <div className="vs">vs</div>
        <div className="team">{p.awayTeam}</div>
      </div>

      <div className="px-pred__lines">
        <div className="line">
          <span className="label">Predicted</span>
          <span className="value">
            {p.predicted}{" "}
            <Badge
              count={`${p.probability}%`}
              style={{ background: `${BRAND}`, boxShadow: "0 1px 0 rgba(0,0,0,.08)" }}
            />
          </span>
        </div>
        <div className="line">
          <span className="label">Actual</span>
          <span className="value">{p.actual}</span>
        </div>
      </div>

      <div className="px-pred__bottom">
        <StatusTag status={p.status} />
        <span className="foot">
          <FiTarget /> Powered by Predixio
        </span>
      </div>
    </Card>
  );
}

export default function PredictionsPreview() {
  const [filter, setFilter] = React.useState("all");
  const filtered = React.useMemo(() => {
    if (filter === "all") return demoPredictions;
    return demoPredictions.filter((p) => p.status.toLowerCase() === filter);
  }, [filter]);

  const correctRate = Math.round(
    (demoPredictions.filter((x) => x.status === "CORRECT").length / demoPredictions.length) * 100
  );

  return (
    <Layout headerStyle={1} footerStyle={1} breadcrumbTitle="Predictions">
      <section className="px-hero">
        <div className="px-hero__inner">
          <span className="px-badge">
            <FiActivity /> Predictive Football Analytics
          </span>
          <h1 className="px-title">Smarter match predictions—built on data</h1>
          <p className="px-sub">
            See how Predixio models turn data into probabilities. Explore recent calls and learn how to
            use predictions responsibly.
          </p>
          <Space wrap size={12} style={{ marginTop: 12 }}>
            <Link to="/premium">
              <Button size="large" type="primary" icon={<FiUserPlus />}>
                Create account
              </Button>
            </Link>
            <Link to="/my/predictions">
              <Button size="large" icon={<FiBarChart2 />} ghost>
                View my predictions
              </Button>
            </Link>
          </Space>
        </div>
      </section>

      <section className="px-section">
        <div className="px-container">
          <Title level={3} className="px-h3">
            How predictions work
          </Title>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Card className="px-how" bordered>
                <div className="icon" style={{ color: "#0ea5e9" }}>
                  <FiDatabase />
                </div>
                <Title level={4}>Data aggregation</Title>
                <Text type="secondary">
                  We aggregate fixtures, player form, xG trends, lineup news and market signals, then
                  normalize every feed.
                </Text>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card className="px-how" bordered>
                <div className="icon" style={{ color: "#9333ea" }}>
                  <FiCpu />
                </div>
                <Title level={4}>Model inference</Title>
                <Text type="secondary">
                  Ensemble models (calibrated) output probabilities for markets like 1X2, O/U, BTTS and
                  more.
                </Text>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card className="px-how" bordered>
                <div className="icon" style={{ color: "#16a34a" }}>
                  <FiShield />
                </div>
                <Title level={4}>Responsible insights</Title>
                <Text type="secondary">
                  Results are informational, not advice. We include uncertainty and publish validation
                  metrics openly.
                </Text>
              </Card>
            </Col>
          </Row>
        </div>
      </section>

      <section className="px-strip">
        <div className="px-container">
          <Row gutter={[16, 16]}>
            <Col xs={12} md={6}>
              <Card className="px-kpi" bordered>
                <Space size={10} align="center">
                  <div className="pill" style={{ color: BRAND, borderColor: `${BRAND}50`, background: `${BRAND}10` }}>
                    <FiTarget />
                  </div>
                  <div>
                    <div className="k-title">Recent calls</div>
                    <div className="k-value">{demoPredictions.length}</div>
                  </div>
                </Space>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card className="px-kpi" bordered>
                <Space size={10} align="center">
                  <div className="pill ok">
                    <FiCheckCircle />
                  </div>
                  <div>
                    <div className="k-title">Correct</div>
                    <div className="k-value">
                      {demoPredictions.filter((p) => p.status === "CORRECT").length}
                    </div>
                  </div>
                </Space>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card className="px-kpi" bordered>
                <Space size={10} align="center">
                  <div className="pill bad">
                    <FiXCircle />
                  </div>
                  <div>
                    <div className="k-title">Incorrect</div>
                    <div className="k-value">
                      {demoPredictions.filter((p) => p.status === "INCORRECT").length}
                    </div>
                  </div>
                </Space>
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card className="px-kpi" bordered>
                <Space size={10} align="center">
                  <div className="pill pend">
                    <FiClock />
                  </div>
                  <div>
                    <div className="k-title">Pending</div>
                    <div className="k-value">
                      {demoPredictions.filter((p) => p.status === "PENDING").length}
                    </div>
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>
          <div className="px-strip__cta">
            <Space wrap>
              <FiTrendingUp />
              <b>Last sample accuracy:</b> {correctRate}% &nbsp;·&nbsp; <Text type="secondary">demo data</Text>
            </Space>
          </div>
        </div>
      </section>

      <section className="px-section">
        <div className="px-container">
          <div className="px-header">
            <div>
              <Title level={3} className="px-h3">
                Recent predictions
              </Title>
              <Text type="secondary">A taste of what members see in real time.</Text>
            </div>
            <Segmented
              value={filter}
              onChange={setFilter}
              options={[
                { label: "All", value: "all" },
                { label: "Correct", value: "correct" },
                { label: "Incorrect", value: "incorrect" },
                { label: "Pending", value: "pending" },
              ]}
            />
          </div>

          <Row gutter={[16, 16]}>
            {filtered.map((p) => (
              <Col key={p.id} xs={24} sm={12} lg={8}>
                <PredictionCard p={p} />
              </Col>
            ))}
          </Row>

          <div className="px-center">
            <Link to="/premium">
              <Button size="large" type="primary" icon={<FiUserPlus />}>
                Join Predixio to unlock full predictions
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="px-section alt">
        <div className="px-container">
          <Row gutter={[16, 16]}>
            <Col xs={24} md={8}>
              <Card className="px-why" bordered>
                <div className="ico" style={{ color: "#f59e0b" }}>
                  <FiZap />
                </div>
                <Title level={4}>Fast & fresh</Title>
                <Text type="secondary">
                  Low-latency feeds with frequent model refreshes during busy match windows.
                </Text>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card className="px-why" bordered>
                <div className="ico" style={{ color: "#06b6d4" }}>
                  <FiBarChart2 />
                </div>
                <Title level={4}>Explainable</Title>
                <Text type="secondary">
                  Clear probabilities and market context so you understand the call, not just the pick.
                </Text>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card className="px-why" bordered>
                <div className="ico" style={{ color: "#10b981" }}>
                  <FiShield />
                </div>
                <Title level={4}>Responsible</Title>
                <Text type="secondary">
                  Predictions are informational only. Guidance and resources for safer usage.
                </Text>
              </Card>
            </Col>
          </Row>
        </div>
      </section>

      <section className="px-cta">
        <div className="px-cta__inner">
          <div className="txt">
            <Title level={3} style={{ color: "#fff", margin: 0 }}>
              Ready to see live predictions?
            </Title>
            <Text style={{ color: "rgba(255,255,255,.85)" }}>
              Create a free account and preview today’s fixtures with probabilities.
            </Text>
          </div>
          <div className="actions">
            <Link to="/premium">
              <Button size="large" type="primary" icon={<FiUserPlus />}>
                Get started
              </Button>
            </Link>
            <Link to="/plans">
              <Button size="large" ghost icon={<FiChevronRight />}>
                View plans
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="px-section">
        <div className="px-container">
          <Title level={3} className="px-h3">
            Frequently asked
          </Title>
          <Collapse
            bordered={false}
            items={[
              {
                key: "1",
                label: "Do you guarantee outcomes?",
                children: (
                  <Text>
                    No—Predixio is informational only. Probabilities express model confidence, not
                    certainty.
                  </Text>
                ),
              },
              {
                key: "2",
                label: "Which markets do you cover?",
                children: <Text>Common markets include 1X2, BTTS, Over/Under, Handicaps and more.</Text>,
              },
              {
                key: "3",
                label: "How often are models updated?",
                children: <Text>Core models retrain weekly; calibrations can refresh multiple times a week.</Text>,
              },
              {
                key: "4",
                label: "Can I access an API?",
                children: <Text>Yes. Higher tiers include API access with documented rate limits and quotas.</Text>,
              },
            ]}
          />
        </div>
      </section>

      <style>{`
        .px-hero {
          position:relative; padding:80px 16px;
          background:
            radial-gradient(1200px 280px at 50% -30%, rgba(59,130,246,0.25), transparent 60%),
            linear-gradient(180deg, #0b1020 0%, #0b1020 40%, #0c1428 100%);
          color:#fff; border-bottom:1px solid rgba(255,255,255,.06);
        }
        .px-hero__inner { max-width:1350px; margin:0 auto; }
        .px-badge {
          display:inline-flex; align-items:center; gap:8px;
          font-size:12px; color:#93c5fd; background:rgba(147,197,253,.10);
          border:1px solid rgba(147,197,253,.30); padding:6px 12px; border-radius:999px;
        }
        .px-title { margin:14px 0 10px; font-size:36px; line-height:1.15; font-weight:900; color:#fff; letter-spacing:-.02em; }
        .px-sub { margin:0; color:rgba(255,255,255,.85); max-width:820px; }

        .px-section { padding:32px 16px; background:#fff; }
        .px-section.alt { background:#f7f7f9; }
        .px-container { max-width:1350px; margin:0 auto; }
        .px-h3 { margin-bottom:12px !important; }

        .px-how, .px-why { border-radius:16px; border:1px solid rgba(0,0,0,.06); }
        .px-how .icon, .px-why .ico { width:42px; height:42px; border-radius:12px; border:1px dashed rgba(0,0,0,.15); display:grid; place-items:center; margin-bottom:10px; background:#fff; }

        .px-strip { background: linear-gradient(180deg,#fbfdff,#f4f7fb); padding:22px 16px; border-top:1px solid rgba(0,0,0,.06); border-bottom:1px solid rgba(0,0,0,.06); }
        .px-strip__cta { margin-top:8px; color:#0f172a; }

        .px-kpi { border-radius:16px; }
        .px-kpi .pill {
          width:36px; height:36px; display:grid; place-items:center; border-radius:10px;
          border:1px solid rgba(0,0,0,.12); background:#fff; color:#0f172a;
        }
        .px-kpi .pill.ok { color:#16a34a; border-color:rgba(22,163,74,.3); background:rgba(22,163,74,.08); }
        .px-kpi .pill.bad { color:#ef4444; border-color:rgba(239,68,68,.3); background:rgba(239,68,68,.08); }
        .px-kpi .pill.pend { color:#f59e0b; border-color:rgba(245,158,11,.3); background:rgba(245,158,11,.08); }
        .k-title { font-size:12px; color:#64748b; }
        .k-value { font-size:20px; font-weight:900; letter-spacing:-.02em; }

        // .px-header { display:flex; align-items:flex-end; justify-content:space-between; gap:12px; margin-bottom:8px; flex-wrap:wrap; }

        .px-pred {
          border-radius:16px; transition:transform .18s ease, box-shadow .18s ease, border-color .18s ease;
          border:1px solid rgba(0,0,0,.06); box-shadow:0 10px 24px rgba(0,0,0,.06);
        }
        .px-pred:hover { transform: translateY(-4px); box-shadow:0 16px 36px rgba(0,0,0,.10); border-color: rgba(0,74,173,.25); }
        .px-pred__top { display:flex; align-items:center; justify-content:space-between; color:#64748b; font-size:12px; }
        .px-pred__teams { display:flex; align-items:center; justify-content:center; gap:8px; margin:6px 0 10px; font-weight:900; color:#0f172a; letter-spacing:-.01em; }
        .px-pred__teams .team { text-align:center; }
        .px-pred__teams .vs { color:#94a3b8; font-weight:700; }
        .px-pred__lines { display:grid; gap:8px; margin-top:6px; }
        .px-pred__lines .line { display:flex; align-items:center; justify-content:space-between; }
        .px-pred__lines .label { font-size:12px; color:#64748b; }
        .px-pred__lines .value { font-weight:700; color:#0f172a; display:flex; align-items:center; gap:8px; }
        .px-pred__bottom { margin-top:10px; display:flex; align-items:center; justify-content:space-between; }
        .px-pred__bottom .foot { color:#64748b; display:inline-flex; align-items:center; gap:6px; font-size:12px; }

        .px-center { text-align:center; margin-top:14px; }

        .px-cta {
          padding:26px 16px;
          background: linear-gradient(90deg, #0b1020, #0c1428);
          border-top: 1px solid rgba(255,255,255,.06);
          border-bottom: 1px solid rgba(255,255,255,.06);
        }
        .px-cta__inner {
          max-width:1350px; margin:0 auto; display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap;
        }
        .px-cta .actions > *:not(:last-child) { margin-right:8px; }

        @media (max-width:520px) {
          .px-title { font-size:28px; }
          .px-cta__inner { flex-direction:column; align-items:flex-start; }
        }
      `}</style>
    </Layout>
  );
}
